import { forwardRef, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import "./Input.css";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  hint?: string;
  error?: string;
  leadingIcon?: ReactNode;
  trailingSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leadingIcon, trailingSlot, id, className, ...rest },
  ref
) {
  // Generate a stable id when the caller doesn't supply one, so label/hint/error
  // can always be wired up correctly rather than silently losing the association.
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={["ui-field", className ?? ""].filter(Boolean).join(" ")}>
      {label && (
        <label className="ui-field__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className={["ui-field__control", error ? "ui-field__control--error" : ""].filter(Boolean).join(" ")}>
        {leadingIcon && <span className="ui-field__icon ui-field__icon--leading">{leadingIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          className="ui-field__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {trailingSlot && <span className="ui-field__slot">{trailingSlot}</span>}
      </div>
      {error ? (
        <p className="ui-field__error" id={errorId}>
          {error}
        </p>
      ) : (
        hint && (
          <p className="ui-field__hint" id={hintId}>
            {hint}
          </p>
        )
      )}
    </div>
  );
});
